import React, { useState, useEffect } from 'react';
import {
  Menu,
  Box,
  List,
  ListItem,
  ListItemText,
  Chip,
  Typography,
  TextField,
  InputAdornment,
  Divider,
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import LocalHospitalIcon from '@mui/icons-material/LocalHospital';
import protocolsData from './mockData/protocols.json';
import protocolGuide from './utils/ProtocolGuide';
import PrismProtocolSidebar from './PrismProtocolSidebar';

export default function PrismProtocolSearch({ anchorEl, open, onClose }) {
  const [searchQuery, setSearchQuery] = useState('');
  const [filteredProtocols, setFilteredProtocols] = useState(protocolsData);
  const [selectedProtocol, setSelectedProtocol] = useState(null);
  const [sidebarOpen, setSidebarOpen] = useState(false);

  useEffect(() => {
    if (!searchQuery.trim()) {
      setFilteredProtocols(protocolsData);
      return;
    }

    const query = searchQuery.toLowerCase();
    const filtered = protocolsData.filter(
      (protocol) =>
        protocol.title.toLowerCase().includes(query) ||
        protocol.category.toLowerCase().includes(query) ||
        protocol.tags.some((tag) => tag.toLowerCase().includes(query)) ||
        protocol.snippet.toLowerCase().includes(query)
    );

    setFilteredProtocols(filtered);
  }, [searchQuery]);

  const handleProtocolClick = (protocol) => {
    setSelectedProtocol(protocol);
    setSidebarOpen(true);
    // Close the search menu when opening sidebar
    onClose();
  };
  
  const handleStartGuide = (protocol) => {
    console.log('[PrismProtocolSearch] Launching protocol guide:', protocol.title);
    
    // Launch the real protocol guide with ghost paths!
    protocolGuide.startProtocolGuide(
      { name: protocol.title, description: protocol.snippet },
      protocol.steps || []
    );
    
    // Close sidebar
    setSidebarOpen(false);
  };

  const handleClose = () => {
    setSearchQuery('');
    onClose();
  };

  const handleCloseSidebar = () => {
    setSidebarOpen(false);
    setSelectedProtocol(null);
  };

  const getScoreColor = (score) => {
    if (score >= 0.9) return 'success';
    if (score >= 0.85) return 'info';
    return 'default';
  };

  return (
    <>
      <Menu
        anchorEl={anchorEl}
        open={open}
        onClose={handleClose}
        PaperProps={{
          sx: {
            mt: 1,
            width: 520,
            maxHeight: '70vh',
            bgcolor: 'rgba(18, 24, 54, 0.98)',
            backdropFilter: 'blur(20px)',
            border: '1px solid rgba(91, 158, 130, 0.3)',
            borderRadius: '12px',
            color: 'white',
          },
        }}
      >
        {/* Header */}
        <Box sx={{ p: 2, pb: 1 }}>
          <Box display="flex" alignItems="center" gap={1}>
            <LocalHospitalIcon sx={{ color: 'rgba(91, 158, 130, 1)', fontSize: 20 }} />
            <Typography variant="subtitle2" fontWeight={600} color="rgba(91, 158, 130, 1)">
              Protocol Search
            </Typography>
            <Chip 
              label="Demo Mode" 
              size="small" 
              sx={{ 
                ml: 'auto',
                bgcolor: 'rgba(228, 165, 39, 0.15)',
                color: 'rgba(228, 165, 39, 1)',
                border: '1px solid rgba(228, 165, 39, 0.3)',
                fontSize: '10px',
                fontWeight: 600,
              }} 
            />
          </Box>
        </Box>
        
        <Divider sx={{ borderColor: 'rgba(255,255,255,0.1)' }} />

        {/* Search Bar */}
        <Box sx={{ p: 2, pb: 1 }}>
          <TextField
            fullWidth
            placeholder="Search clinical protocols..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            size="small"
            autoFocus
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon sx={{ color: 'rgba(255,255,255,0.5)' }} />
                </InputAdornment>
              ),
              sx: {
                color: 'white',
                bgcolor: 'rgba(255,255,255,0.05)',
                border: '1px solid rgba(255,255,255,0.1)',
                borderRadius: '8px',
                '& .MuiOutlinedInput-notchedOutline': {
                  border: 'none',
                },
                '&:hover': {
                  bgcolor: 'rgba(255,255,255,0.08)',
                },
                '&.Mui-focused': {
                  bgcolor: 'rgba(255,255,255,0.1)',
                  border: '1px solid rgba(91, 158, 130, 0.5)',
                },
              },
            }}
          />
          <Typography variant="caption" color="rgba(255,255,255,0.5)" sx={{ mt: 0.5, display: 'block' }}>
            Try: diabetes, hypertension, depression, COPD, heart failure
          </Typography>
        </Box>

        {/* Results List */}
        <List sx={{ p: 0, maxHeight: '400px', overflowY: 'auto' }}>
          {filteredProtocols.length === 0 ? (
            <Box sx={{ p: 4, textAlign: 'center' }}>
              <SearchIcon sx={{ fontSize: 48, color: 'rgba(255,255,255,0.3)', mb: 2 }} />
              <Typography color="rgba(255,255,255,0.7)">
                No protocols found matching &quot;{searchQuery}&quot;
              </Typography>
            </Box>
          ) : (
            filteredProtocols.slice(0, 8).map((protocol) => (
              <React.Fragment key={protocol.id}>
                <ListItem
                  button
                  onClick={() => handleProtocolClick(protocol)}
                  sx={{
                    py: 1.5,
                    px: 2,
                    '&:hover': {
                      bgcolor: 'rgba(91, 158, 130, 0.15)',
                    },
                  }}
                >
                  <ListItemText
                    primary={
                      <Box display="flex" alignItems="center" gap={1} mb={0.5}>
                        <Typography variant="body2" fontWeight={600} color="white">
                          {protocol.title}
                        </Typography>
                        <Chip
                          label={`${(protocol.score * 100).toFixed(0)}%`}
                          size="small"
                          sx={{
                            bgcolor: protocol.score >= 0.9 ? 'rgba(76, 175, 80, 0.2)' : 'rgba(33, 150, 243, 0.2)',
                            color: protocol.score >= 0.9 ? '#4caf50' : '#2196f3',
                            fontWeight: 600,
                            fontSize: '11px',
                          }}
                        />
                      </Box>
                    }
                    secondary={
                      <Box>
                        <Typography variant="caption" color="rgba(255,255,255,0.7)" paragraph sx={{ mb: 1 }}>
                          {protocol.snippet}
                        </Typography>
                        <Box display="flex" gap={0.5} flexWrap="wrap">
                          <Chip 
                            label={protocol.category} 
                            size="small" 
                            sx={{ 
                              bgcolor: 'rgba(91, 158, 130, 0.2)',
                              color: 'rgba(91, 158, 130, 1)',
                              border: '1px solid rgba(91, 158, 130, 0.3)',
                              fontSize: '10px',
                            }}
                          />
                          {protocol.tags.slice(0, 2).map((tag) => (
                            <Chip 
                              key={tag} 
                              label={tag} 
                              size="small" 
                              sx={{ 
                                bgcolor: 'rgba(255,255,255,0.05)',
                                color: 'rgba(255,255,255,0.7)',
                                border: '1px solid rgba(255,255,255,0.1)',
                                fontSize: '10px',
                              }}
                            />
                          ))}
                        </Box>
                      </Box>
                    }
                  />
                </ListItem>
                <Divider sx={{ borderColor: 'rgba(255,255,255,0.05)' }} />
              </React.Fragment>
            ))
          )}
        </List>
        
        {filteredProtocols.length > 8 && (
          <Box sx={{ p: 1.5, textAlign: 'center', borderTop: '1px solid rgba(255,255,255,0.1)' }}>
            <Typography variant="caption" color="rgba(255,255,255,0.5)">
              Showing 8 of {filteredProtocols.length} results
            </Typography>
          </Box>
        )}
      </Menu>

      {/* Protocol Sidebar - slides out from right */}
      <PrismProtocolSidebar
        open={sidebarOpen}
        protocol={selectedProtocol}
        onClose={handleCloseSidebar}
        onStartGuide={handleStartGuide}
      />
    </>
  );
}
